import { GroupWithCommunity } from '@front-app/services/GroupsApi/GroupModel';
import { GroupsStore } from '@front-app/store/singletons/Groups';
import { defineStore } from 'pinia';
import { DigestFormOutput } from '../components/digestForm';
import { FullDigest } from '../model';
import { digestsApi } from '../services/DigestsApi';
import {
	ensureFutureEndDate,
	isoDateToDigestDate,
	toApiCreatePayload,
	toApiUpdatePayload,
	toFullDigest,
} from '../util/parse';
import { sortDigestsByStatusAndName } from '../util/sort';
import { isFullDigestResponse } from '../util/types';

export interface DigestsStoreState {
	digests: FullDigest[];
}

export const useDigestsStore = defineStore('groupDigestsStore', {
	state: (): DigestsStoreState => ({
		digests: [],
	}),
	getters: {
		groupDigests(state) {
			return (requestedGroupId: string) => state.digests
				.filter(({ groupId }) => groupId === requestedGroupId)
				.sort(sortDigestsByStatusAndName);
		},
		getById(state) {
			return (requestedGroupId: string, requestedDigestId: string) => state.digests
				.find(({ id, groupId }) => id === requestedDigestId && groupId === requestedGroupId);
		},
	},
	actions: {
		storeDigests(digests: FullDigest[]) {
			this.$patch((state) => {
				const digestIds = digests.map(({ id }) => id);
				const oldDigests = state.digests.filter(({ id }) => !digestIds.includes(id));
				state.digests = oldDigests.concat(digests);
			});
		},
		async fetchDigestsForGroup(groupId: string): Promise<void> {
			const { data } = await digestsApi.digestList([groupId]);
			this.storeDigests(data.filter(isFullDigestResponse).map(toFullDigest));
		},
		async fetchFullDigest(digestId: string): Promise<void> {
			const { data } = await digestsApi.digestRead(digestId);
			if (isFullDigestResponse(data)) {
				this.storeDigests([toFullDigest(data)]);
			}
		},
		async updateDigest(groupId: string, digestId: string, digest: DigestFormOutput): Promise<void> {
			const payload = toApiUpdatePayload(groupId, digestId, digest);
			const { data } = await digestsApi.digestUpdate(digestId, payload);
			if (isFullDigestResponse(data)) {
				this.storeDigests([toFullDigest(data)]);
			}
		},
		async create(groupId: string, digest: DigestFormOutput): Promise<string> {
			const group = await getGroup(groupId);
			if (!group) {
				throw new Error(`You do not have access to create digests on the target group: ${groupId}`);
			}
			const payload = toApiCreatePayload(group, digest);
			const { data } = await digestsApi.digestCreate(payload);
			if (!isFullDigestResponse(data)) {
				throw new Error(`Error creating digest for group "${groupId}" with data: ${digest}`);
			}
			this.storeDigests([toFullDigest(data)]);
			return data.id;
		},
		async setDeactivation(groupId: string, digestId: string, isDeactivated: boolean): Promise<void> {
			const digest = this.getById(groupId, digestId);
			if (!digest) {
				throw new Error('Could not update missing digest. Please fetch the digest first');
			}
			const payload = {
				groupId,
				isDeactivated,
				id: digestId,
				endDate: isoDateToDigestDate(isDeactivated ? digest.endDate : ensureFutureEndDate(digest.endDate)),
			};
			const { data } = await digestsApi.digestUpdate(digestId, payload);
			if (isFullDigestResponse(data)) {
				this.storeDigests([toFullDigest(data)]);
			}
		},
	},
});

async function getGroup(groupId: string): Promise<GroupWithCommunity | undefined> {
	const group = GroupsStore.getInstance().groupById(groupId);
	if (group) {
		return group;
	}
	await GroupsStore.getInstance().fetchGroup(groupId);
	return GroupsStore.getInstance().groupById(groupId);
}
